import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Logout as LogoutIcon,
  //HealthAndSafety as HealthIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  LocalHospital as DoctorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../AlertContext';

// eslint-disable-next-line react/prop-types
const ModernNavbar = ({ is_admin }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { logout } = useAlert();

  const session = JSON.parse(localStorage.getItem('session') || '{}');

  const userMenuItems = [
    { text: 'Inicio', icon: <HomeIcon />, path: '/' },
    { text: 'Agendar', icon: <CalendarIcon />, path: '/calendar' },
    { text: 'Beneficiarios', icon: <PeopleIcon />, path: '/beneficiaries' },
    { text: 'PQR', icon: <AssignmentIcon />, path: '/pqrsdf' },
    { text: 'Perfil', icon: <PersonIcon />, path: '/profile' },
  ];

  const adminMenuItems = [
    { text: 'Tareas', icon: <AssignmentIcon />, path: '/admin-tasks' },
    { text: 'Usuarios', icon: <PeopleIcon />, path: '/admin-users' },
    { text: 'Doctores', icon: <DoctorIcon />, path: '/admin-doctors' },
    { text: 'Registrar', icon: <PersonAddIcon />, path: '/admin-register' },
  ];

  const menuItems = is_admin ? adminMenuItems : userMenuItems;

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    logout();
    setDrawerOpen(false);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    return names.length >= 2
      ? `${names[0][0]}${names[1][0]}`.toUpperCase()
      : names[0][0].toUpperCase();
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 4 } }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {/* <HealthIcon sx={{ mr: 1, fontSize: 32 }} />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
              }}
            >
              Clodan
            </Typography>*/}
            <img
              src="/white-logo.png"
              alt="Clodan Logo"
              style={{ height: 40 }}
            />
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  color="inherit"
                  startIcon={item.icon}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}

              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.2)' }}
              />

              <Button
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Salir
              </Button>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              onClick={() => setDrawerOpen(true)}
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: 'linear-gradient(180deg, #94B754 0%, #7DA647 100%)',
            color: 'white',
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              backgroundColor: 'rgba(255,255,255,0.2)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
            }}
          >
            {getInitials(session.user_name)}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {session.user_name || 'Usuario'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {is_admin ? (
              <>
                <AdminIcon
                  sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }}
                />
                Administrador
              </>
            ) : (
              'Usuario'
            )}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

        {/* Menu Items */}
        <List sx={{ px: 2, py: 1 }}>
          {menuItems.map((item) => (
            <ListItem
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: 500,
                }}
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ flexGrow: 1 }} />

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

        {/* Logout Button */}
        <List sx={{ px: 2, py: 1 }}>
          <ListItem
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Cerrar Sesión"
              primaryTypographyProps={{
                fontWeight: 500,
              }}
            />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default ModernNavbar;
